import React, { useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  Add,
  Check,
  Close,
  ContentCopy,
  Delete,
  Edit,
  Remove,
} from '@mui/icons-material'
import { GenericError, ObjectNotFound } from '../error-pages'
import { ago } from './api'
import { Empty, IconTile, Page, Panel, Row, useBrand, useNow } from './ui'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsHeartbeats($id: ID!) {
    service(id: $id) {
      id
      name
      heartbeatMonitors {
        id
        name
        timeoutMinutes
        lastState
        lastHeartbeat
        href
        additionalDetails
      }
    }
  }
`
const createMutation = gql`
  mutation ($input: CreateHeartbeatMonitorInput!) {
    createHeartbeatMonitor(input: $input) {
      id
    }
  }
`
const updateMutation = gql`
  mutation ($input: UpdateHeartbeatMonitorInput!) {
    updateHeartbeatMonitor(input: $input)
  }
`
const deleteMutation = gql`
  mutation ($id: ID!) {
    deleteAll(input: [{ type: heartbeatMonitor, id: $id }])
  }
`

type Monitor = {
  id: string
  name: string
  timeoutMinutes: number
  lastState: 'inactive' | 'healthy' | 'unhealthy'
  lastHeartbeat?: string | null
  href: string
  additionalDetails: string
}

const MaxDetails = 6 * 1024

// minutes as words: 90 -> "1 h 30 min", 1440 -> "1 day"
export function duration(min: number): string {
  const d = Math.floor(min / 1440)
  const h = Math.floor((min % 1440) / 60)
  const m = min % 60
  const parts = []
  if (d) parts.push(d === 1 ? '1 day' : `${d} days`)
  if (h) parts.push(`${h} h`)
  if (m) parts.push(`${m} min`)
  return parts.join(' ') || '0 min'
}

const gqlMessage = (e?: { message: string }): string =>
  (e?.message ?? '').replace('[GraphQL] ', '')

const presets = [5, 15, 30, 60, 360, 1440]
const units = [
  { value: 1, label: 'minutes' },
  { value: 60, label: 'hours' },
  { value: 1440, label: 'days' },
]

function HeartbeatDialog(props: {
  serviceID: string
  monitor?: Monitor
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const m = props.monitor
  const [name, setName] = useState(m?.name ?? '')
  const [minutes, setMinutes] = useState(m?.timeoutMinutes ?? 15)
  const [custom, setCustom] = useState(
    m ? !presets.includes(m.timeoutMinutes) : false,
  )
  const initialUnit =
    units
      .slice()
      .reverse()
      .find((u) => (m?.timeoutMinutes ?? 15) % u.value === 0)?.value ?? 1
  const [unit, setUnit] = useState(initialUnit)
  const [amount, setAmount] = useState(
    String((m?.timeoutMinutes ?? 15) / initialUnit),
  )
  const [details, setDetails] = useState(m?.additionalDetails ?? '')
  const [createStatus, create] = useMutation(createMutation)
  const [updateStatus, update] = useMutation(updateMutation)
  const status = m ? updateStatus : createStatus

  const timeout = custom ? Math.round(Number(amount) * unit) : minutes
  const timeoutOK =
    Number.isFinite(timeout) && timeout >= 5 && timeout <= 540000
  const valid = name.trim() !== '' && timeoutOK && details.length <= MaxDetails

  const save = (): void => {
    const input = {
      name: name.trim(),
      timeoutMinutes: timeout,
      additionalDetails: details,
    }
    const done = (r: { error?: unknown }): void => {
      if (!r.error) props.onClose()
    }
    if (m) {
      update(
        { input: { id: m.id, ...input } },
        { additionalTypenames: ['HeartbeatMonitor'] },
      ).then(done)
    } else {
      create(
        { input: { serviceID: props.serviceID, ...input } },
        { additionalTypenames: ['HeartbeatMonitor', 'Service'] },
      ).then(done)
    }
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {m ? 'Edit heartbeat monitor' : 'New heartbeat monitor'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label='Name'
          placeholder='e.g. Nightly backup'
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 2.5 }}
        />

        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Raise an alert if no beat arrives within
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {presets.map((p) => {
            const on = !custom && minutes === p
            return (
              <Chip
                key={p}
                label={duration(p)}
                color={on ? 'primary' : 'default'}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => {
                  setCustom(false)
                  setMinutes(p)
                }}
              />
            )
          })}
          <Chip
            label='Custom'
            color={custom ? 'primary' : 'default'}
            variant={custom ? 'filled' : 'outlined'}
            onClick={() => setCustom(true)}
          />
        </Box>
        {custom && (
          <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
            <TextField
              size='small'
              type='number'
              label='Every'
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ width: 120 }}
            />
            <TextField
              select
              size='small'
              value={unit}
              onChange={(e) => {
                const u = Number(e.target.value)
                setUnit(u)
              }}
              sx={{ width: 140 }}
            >
              {units.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {u.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
        <Typography
          sx={{
            fontSize: 13,
            mb: 2.5,
            color: timeoutOK ? c.muted : 'error.main',
          }}
        >
          {timeoutOK
            ? `If nothing arrives for ${duration(timeout)}, an alert is raised.`
            : 'Pick at least 5 minutes (and at most 375 days).'}
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label='Notes for the alert (optional)'
          placeholder='What to check when this monitor misses a beat'
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          error={details.length > MaxDetails}
          helperText={`Shown in the alert. Markdown works. ${details.length}/${MaxDetails}`}
        />

        {status.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(status.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || status.fetching}
          onClick={save}
        >
          {m ? 'Save' : 'Create monitor'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DeleteDialog(props: {
  monitor: Monitor
  onClose: () => void
}): React.JSX.Element {
  const [status, del] = useMutation(deleteMutation)
  return (
    <Dialog open onClose={props.onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Delete “{props.monitor.name}”?</DialogTitle>
      <DialogContent>
        <Typography>
          Its URL stops working and it will no longer raise alerts.
        </Typography>
        {status.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(status.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          color='error'
          disabled={status.fetching}
          onClick={() =>
            del(
              { id: props.monitor.id },
              { additionalTypenames: ['HeartbeatMonitor', 'Service'] },
            ).then((r) => {
              if (!r.error) props.onClose()
            })
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// HeartbeatsPage replaces GoAlert's heartbeat monitor list: one row per
// monitor with its state, copyable URL, edit and delete.
export default function HeartbeatsPage(props: {
  serviceID: string
}): React.JSX.Element {
  const c = useBrand()
  const theme = useTheme()
  const now = useNow()
  const [{ data, error, fetching }] = useQuery({
    query,
    variables: { id: props.serviceID },
    context: noSuspense,
  })
  const [editing, setEditing] = useState<Monitor | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Monitor | null>(null)
  const [copied, setCopied] = useState('')

  if (error) return <GenericError error={error.message} />
  if (!data && fetching) return <Page title='Heartbeat monitors'>{null}</Page>
  if (!data?.service) return <ObjectNotFound />

  const s = data.service
  const monitors: Monitor[] = [...s.heartbeatMonitors].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  )

  const copy = (m: Monitor): void => {
    navigator.clipboard.writeText(m.href).then(() => {
      setCopied(m.id)
      setTimeout(() => setCopied(''), 1500)
    })
  }

  const state = {
    healthy: {
      tone: 'ok' as const,
      icon: <Check />,
      label: 'Healthy',
      color: c.ok,
    },
    unhealthy: {
      tone: 'bad' as const,
      icon: <Close />,
      label: 'Missed',
      color: theme.palette.error.main,
    },
    inactive: {
      tone: 'muted' as const,
      icon: <Remove />,
      label: 'Waiting',
      color: c.muted,
    },
  }

  const newButton = (
    <Button
      variant='contained'
      startIcon={<Add />}
      onClick={() => setEditing('new')}
    >
      New monitor
    </Button>
  )

  return (
    <Page
      title='Heartbeat monitors'
      subtitle={
        <React.Fragment>
          A sender POSTs to the monitor&apos;s URL on a schedule. If nothing
          arrives within the timeout, <b>{s.name}</b> raises an alert.
        </React.Fragment>
      }
      actions={monitors.length ? newButton : undefined}
      maxWidth={960}
    >
      <Stack spacing={2.5}>
        <Panel flush>
          {monitors.length === 0 ? (
            <Empty
              title='No heartbeat monitors yet'
              text='Add one for every job or device that should check in regularly.'
              action={newButton}
            />
          ) : (
            monitors.map((m) => {
              const st = state[m.lastState] ?? state.inactive
              return (
                <Row
                  key={m.id}
                  leading={<IconTile tone={st.tone}>{st.icon}</IconTile>}
                  title={m.name}
                  subtitle={
                    <React.Fragment>
                      Every {duration(m.timeoutMinutes)}
                      {' · '}
                      {m.lastHeartbeat
                        ? `last beat ${ago(m.lastHeartbeat, now).replace(/^(?!just now)(.*)$/, '$1 ago')}`
                        : 'no beat received yet'}
                    </React.Fragment>
                  }
                  meta={
                    <Chip
                      size='small'
                      label={st.label}
                      variant='outlined'
                      sx={{ color: st.color, borderColor: st.color }}
                    />
                  }
                  trailing={
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={<ContentCopy fontSize='small' />}
                        onClick={() => copy(m)}
                      >
                        {copied === m.id ? 'Copied' : 'Copy URL'}
                      </Button>
                      <Tooltip title='Edit'>
                        <IconButton
                          size='small'
                          aria-label={`Edit ${m.name}`}
                          onClick={() => setEditing(m)}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Delete'>
                        <IconButton
                          size='small'
                          aria-label={`Delete ${m.name}`}
                          onClick={() => setDeleting(m)}
                        >
                          <Delete fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />
              )
            })
          )}
        </Panel>

        {monitors.length > 0 && (
          <Panel
            title='Sending a heartbeat'
            subtitle='Any HTTP POST to the URL counts; the body is ignored.'
          >
            <Box
              component='pre'
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: '10px',
                bgcolor: c.bg,
                border: `1px solid ${c.line}`,
                fontSize: 13,
                overflowX: 'auto',
                color: c.ink,
              }}
            >
              curl -X POST {monitors[0].href}
            </Box>
          </Panel>
        )}
      </Stack>

      {editing && (
        <HeartbeatDialog
          serviceID={s.id}
          monitor={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <DeleteDialog monitor={deleting} onClose={() => setDeleting(null)} />
      )}
    </Page>
  )
}
